import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import { useExperience, ALT } from '@/store/experience';
import { QUALITY } from '@/lib/quality';
import { buildCloudData } from './cloudData';
import { SkyDome } from './SkyDome';
import { CloudField } from './CloudField';
import { Particles } from './Particles';
import { CoinPlane } from './CoinPlane';
import { CameraRig } from './CameraRig';

export function SkyScene({ cloudCanvas, video }) {
  const quality = useExperience((s) => s.quality);
  const q = QUALITY[quality];
  const data = useMemo(() => buildCloudData(q.clouds, q.frontClouds), [q]);
  const texture = useMemo(() => {
    // No mipmaps: coarse levels bleed alpha across atlas tiles and paint faint quads
    const t = new THREE.CanvasTexture(cloudCanvas);
    t.minFilter = THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = false;
    return t;
  }, [cloudCanvas]);

  const off = useMemo(() => new URLSearchParams(window.location.search).get('nolayer') || '', []);

  return (
    <div className="fixed inset-0 z-0" data-testid="sky-canvas">
      <Canvas
        dpr={q.dpr}
        gl={{ antialias: false, alpha: false, powerPreference: 'high-performance', stencil: false }}
        camera={{ fov: 60, near: 0.1, far: 1200, position: [0, ALT.PLANE, 0] }}
        onCreated={({ gl }) => { gl.setClearColor('#061a3d'); gl.toneMapping = THREE.NoToneMapping; }}
      >
        {!off.includes('sky') && <SkyDome />}
        {!off.includes('back') && <CloudField clouds={data.back} texture={texture} layer="back" />}
        {!off.includes('particles') && <Particles count={q.particles} />}
        {video && !off.includes('coin') && <CoinPlane video={video} />}
        {!off.includes('front') && <CloudField clouds={data.front} texture={texture} layer="front" />}
        <CameraRig />
      </Canvas>
    </div>
  );
}
